import React, { useCallback } from 'react';

import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import some from 'lodash/some';

import { isDNSCompliantString } from '../../../utils/text_utils';

import useModelSearch from '../../models/ModelSearchContext';
import usePipelineCreationContext from '../PipelineCreationContext';

import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import Label from '../../common/Labels/Label';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import TextInputWithError from '../../common/TextInputs/TextInputWithError/TextInputWithError';

import ModelSingleSelectionView from '../../models/ModelSingleSelectionView/ModelSingleSelectionView';

const groupModelsById = (models = []) => {
  const modelGroupings = groupBy(models, ({ modelID }) => modelID);
  const modelKeys = Object.keys(modelGroupings);
  return modelKeys.map((modelID) => {
    const latestModelVersion = modelGroupings[modelID][0]; // TODO - Assumes the list is presorted - check if this is valid
    return ({
      modelID,
      latestModelVersion,
      sha: latestModelVersion.sha,
      modelVersion: latestModelVersion.modelVersion,
      createdAt: latestModelVersion?.createdAt,
      updatedAt: latestModelVersion?.updatedAt,
    })
  });
}

export const PipelineStepSelectionFrame = ({
  cardTitle,
  handleNext,
  models,
  loading,
  error,
  pipelineName,
  pipelineSteps,
  addStep,
  removeStep,
  setPipelineName,
  onClose,
}) => {
  const isModelSelected = useCallback(modelID => some(pipelineSteps, item => get(item, 'ModelInference.models[0].name') === modelID), [pipelineSteps])

  const onSelect = useCallback((selectedModelId) => {
    if (isModelSelected(selectedModelId)) {
      removeStep(pipelineSteps.findIndex(({ modelID }) => modelID === selectedModelId));
    } else {
      addStep(models.find(({ modelID }) => modelID === selectedModelId));
    }
  }, [addStep, isModelSelected, models, pipelineSteps, removeStep]);

  const onEntryValidation = (entryStr) => {
    if (!entryStr?.length) {
      return false;
    }

    return isDNSCompliantString(entryStr);
  };

  const displayError = 'Please enter a valid name for your pipeline. Note: Pipeline names must be DNS compliant.';
  return (
    <CardView className="flex flex-col">
      <div className="flex justify-between items-center mb-2">
        <Label size="lg" bold>{cardTitle}</Label>
        <Button
          variant="close"
          iconSize={24}
          textColor="border-dark"
          hoverTextColor="border-darker"
          activeTextColor="info"
          onClick={onClose}
        />
      </div>
      <Label className="mb-2 underline" bold>Pipeline Name</Label>
      <TextInputWithError
        className="mb-4"
        placeholder="Please enter your pipeline name here."
        onEntryValidation={onEntryValidation}
        onChange={setPipelineName}
        value={pipelineName}
        errorMessage={displayError}
        block
      />
      <Label className="mb-2" bold>Add Pipeline Step 1</Label>
      <div className="flex border rounded overflow-scroll flex-grow relative">
        {
          loading ? (
            <LoadingSpinner
              animationKey="step-selection-loader"
              loadingText="Loading Models..."
              useOverlay
            />
          ) : (
            <ModelSingleSelectionView
              models={models}
              error={error}
              isModelSelected={isModelSelected}
              onSelect={onSelect}
            />
          )
        }
      </div>
      <div className="w-full flex justify-end my-2">
        <Button
          size="large"
          onClick={handleNext} 
          disabled={!pipelineSteps?.length || !pipelineName?.length}
        >
          Next
        </Button>
      </div>
    </CardView>
  )
}

const ContextualStepSelectionFrame = ({ cardTitle, handleNext, onClose }) => {
  const {
    pipelineName,
    pipelineSteps,
    addStep,
    removeStep,
    setPipelineName,
  } = usePipelineCreationContext();
  const { models, loading, error } = useModelSearch();
  const groupedModels = models?.length ? groupModelsById(models) : null;

  return (
    <PipelineStepSelectionFrame
      cardTitle={cardTitle}
      pipelineName={pipelineName}
      pipelineSteps={pipelineSteps}
      loading={loading}
      error={error}
      addStep={addStep}
      removeStep={removeStep}
      setPipelineName={setPipelineName}
      models={groupedModels}
      handleNext={handleNext}
      onClose={onClose}
    />
  )
}

export default ContextualStepSelectionFrame;
